import { useState, useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { addShoppingItem } from "@/services/shopping/items/index";
import { UserProduct, UserCategory } from "@/types";
import { useAuth } from "@/context/auth/AuthContext";
import { useOfflineShoppingItems } from "@/hooks/useOfflineShoppingItems";
import logger from '@/utils/logger';
import { validateQuantity } from "@/utils/security";
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import { useModalStore } from "@/services/modalStore";
import { useModalBackButton } from "@/hooks/useModalBackButton";
import { useOfflineProducts } from "@/hooks/useOfflineProducts";
import { useOfflineCategories } from "@/hooks/useOfflineCategories";
import { networkService } from "@/services/network.service";
import { safeToastError } from "@/components/ui/toastManager";

interface UseAddItemDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  listId: string;
  rapidInsert?: boolean;
  onSuccess?: () => void;
}

export const useAddItemDialog = ({
  isOpen,
  onOpenChange,
  listId,
  rapidInsert = false,
  onSuccess,
}: UseAddItemDialogProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [forceNewProductForm, setForceNewProductForm] = useState(false);
  const [unit, setUnit] = useState("pz");
  const [category, setCategory] = useState("none");
  const queryClient = useQueryClient();
  const { user } = useAuth();
  // Use shopping items from the same query key used across the app so
  // highlights stay in sync when items are removed from the list.
  const { shoppingItems } = useOfflineShoppingItems(listId);
  const [addedProductsLocal, setAddedProductsLocal] = useState<string[]>([]);
  const { theme } = useThemeConfig();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const lastScrollTop = useRef<number>(0);
  const modalStore = useModalStore();
  const isOnline = networkService.isOnline();
  const [filter, setFilter] = useState<string>('all');

  const { handleOpenChange } = useModalBackButton({
    isOpen,
    modalName: "add-item-dialog",
    onOpenChange: (open) => {
      if (!open) {
        modalStore.closeModal();
        setSearchTerm("");
        setForceNewProductForm(false);
        setSelectedProduct(null);
        setQuantity("1");
        setUnit("pz");
        setAddedProductsLocal([]);
      }
      onOpenChange(open);
    },
  });

  const { products = [], isLoading: productsLoading, createProduct } = useOfflineProducts();
  const [isCreating, setIsCreating] = useState(false);
  const { categories = [], isLoading: categoriesLoading } = useOfflineCategories();
  const visibleCategories = useMemo(
    () => categories.filter(cat => cat.visibile !== false),
    [categories]
  );

  const addedProductIds = useMemo(() => {
    const fromList = shoppingItems.map(item => item.product_id);
    return Array.from(new Set([...fromList, ...addedProductsLocal]));
  }, [shoppingItems, addedProductsLocal]);

  const [selectedProduct, setSelectedProduct] = useState<UserProduct | null>(null);
  const [quantity, setQuantity] = useState("1");
  const [quantityPresets] = useState(["1", "2", "3", "4", "5", "6"]);

  const sanitizeQuantity = (value: string | number): string | null => {
    const validation = validateQuantity(value);

    if (!validation.isValid) {
      console.error("Quantità non valida:", validation.error, value);
      return null;
    }

    return validation.sanitized;
  };

  // Annulla l'inserimento ottimistico quando addShoppingItem fallisce
  // (la funzione ritorna null senza lanciare): rimuove l'highlight locale
  // e avvisa l'utente, altrimenti il prodotto sembra aggiunto ma non esiste.
  const rollbackOptimisticItem = (productId: string) => {
    setAddedProductsLocal(prev => prev.filter(id => id !== productId));
    safeToastError("Impossibile aggiungere il prodotto", {
      description: "Riprova tra qualche istante.",
    });
  };

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      setForceNewProductForm(false);
      setSelectedProduct(null);
      setQuantity("1");
      setUnit("pz");
      setAddedProductsLocal([]);
    }
  }, [isOpen]);

  // Remove locally tracked products if they are no longer in the shopping list
  useEffect(() => {
    setAddedProductsLocal(prev => {
      const filtered = prev.filter(id =>
        shoppingItems.some(item => item.product_id === id)
      );
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [shoppingItems]);

  const groupedProducts = useMemo(() => {
    const groups: Record<string, UserProduct[]> = {};

    const categoryItems = visibleCategories.map(cat => ({
      ...cat,
      userId: cat.userId || user?.id || ''
    })) as UserCategory[];

    categoryItems.forEach((cat) => {
      groups[cat.id] = [];
    });
    groups["other"] = [];

    const visibleCategoryIds = categoryItems.map(cat => cat.id);

    products.forEach((product) => {
      if (product.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        if (!product.categoryId || visibleCategoryIds.includes(product.categoryId)) {
          const groupKey = product.categoryId ?? "other";
          if (!groups[groupKey]) groups[groupKey] = [];
          groups[groupKey].push(product);
        }
      }
    });

    Object.keys(groups).forEach((key) => {
      if (groups[key].length === 0) {
        delete groups[key];
      } else {
        groups[key].sort((a, b) => a.name.localeCompare(b.name));
      }
    });

    return groups;
  }, [products, visibleCategories, searchTerm, user?.id]);

  const filteredGroupedProducts = useMemo(() => {
    if (filter === 'all') return groupedProducts;

    const filtered: Record<string, UserProduct[]> = {};
    Object.keys(groupedProducts).forEach((key) => {
      const prods = groupedProducts[key].filter((prod) => {
        const item = shoppingItems.find((i) => i.product_id === prod.id);
        if (filter === 'active') return item && !item.checked;
        if (filter === 'completed') return item && item.checked;
        return true;
      });
      if (prods.length > 0) {
        filtered[key] = prods;
      }
    });
    return filtered;
  }, [groupedProducts, filter, shoppingItems]);

  const hasProducts = Object.keys(groupedProducts).length > 0;

  useEffect(() => {
    if (searchTerm.trim() !== "" && !hasProducts) {
      setForceNewProductForm(true);
    } else {
      setForceNewProductForm(false);
    }
  }, [searchTerm, hasProducts]);

  useEffect(() => {
    if (forceNewProductForm && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [forceNewProductForm]);

  const handleProductSelect = (prod: UserProduct) => {
    if (scrollRef.current) {
      lastScrollTop.current = scrollRef.current.scrollTop;
      logger.log("[SCROLL] handleProductSelect - scroll salvato:", scrollRef.current?.scrollTop);
    }

    if (rapidInsert) {
      const sanitizedQuantity = sanitizeQuantity(prod.defaultQuantity ?? "1");

      if (!sanitizedQuantity) {
        return;
      }

      const unitValue = prod.defaultUnit || "pz";

      setAddedProductsLocal(prev => [...prev, prod.id]);

      void addShoppingItem(listId, {
        product_id: prod.id,
        quantity: sanitizedQuantity,
        unit: unitValue,
        category: prod.categoryId ?? "other",
      })
        .then((result) => {
          if (!result) {
            rollbackOptimisticItem(prod.id);
            return;
          }
          // Leggi lo stato di rete al completamento, non quello catturato al
          // render: una transizione offline→online nel frattempo non deve far
          // saltare l'invalidazione della lista (#1432).
          if (networkService.isOnline()) {
            return queryClient.invalidateQueries({ queryKey: ['shopping-list', listId] });
          }
        })
        .catch((error) => {
          rollbackOptimisticItem(prod.id);
          logger.logError("useAddItemDialog","Error adding item",{additionalInfo:error});
        });

      setSearchTerm("");
      setQuantity("1");
      setUnit("pz");

      onSuccess?.();
      return;
    }

    const defaultQuantity = sanitizeQuantity(prod.defaultQuantity ?? "1") ?? "1";

    setSelectedProduct(prod);
    setQuantity(defaultQuantity);
    setUnit(prod.defaultUnit || "pz");
  };

  const handleAdd = () => {
    if (!selectedProduct) return;

    const sanitizedQuantity = sanitizeQuantity(quantity);

    if (!sanitizedQuantity) {
      return;
    }

    const addedProductId = selectedProduct.id;
    setAddedProductsLocal(prev => [...prev, addedProductId]);

    void addShoppingItem(listId, {
      product_id: addedProductId,
      quantity: sanitizedQuantity,
      unit,
      category: selectedProduct.categoryId ?? "other",
    })
      .then((result) => {
        if (!result) {
          rollbackOptimisticItem(addedProductId);
          return;
        }
        // Stato di rete letto al completamento (non al render): vedi #1432.
        if (networkService.isOnline()) {
          return queryClient.invalidateQueries({ queryKey: ['shopping-list', listId] });
        }
      })
      .catch((error) => {
        rollbackOptimisticItem(addedProductId);
        logger.logError("useAddItemDialog","Error adding item",{additionalInfo:error});
      });

    setSearchTerm("");
    setSelectedProduct(null);
    setQuantity("1");
    setUnit("pz");

    onSuccess?.();
  };

  const handleAddClose = () => {
    if (!selectedProduct) return;

    const sanitizedQuantity = sanitizeQuantity(quantity);

    if (!sanitizedQuantity) {
      return;
    }

    const addedProductId = selectedProduct.id;
    setAddedProductsLocal(prev => [...prev, addedProductId]);

    void addShoppingItem(listId, {
      product_id: addedProductId,
      quantity: sanitizedQuantity,
      unit,
      category: selectedProduct.categoryId ?? "other",
    })
      .then((result) => {
        if (!result) {
          rollbackOptimisticItem(addedProductId);
          return;
        }
        // Stato di rete letto al completamento (non al render): vedi #1432.
        if (networkService.isOnline()) {
          return queryClient.invalidateQueries({ queryKey: ['shopping-list', listId] });
        }
      })
      .catch((error) => {
        rollbackOptimisticItem(addedProductId);
        logger.logError("useAddItemDialog","Error adding item",{additionalInfo:error});
      });

    onSuccess?.();
    handleOpenChange(false);
  };

  const handleCreateProduct = async (closeAfter: boolean) => {
    if (!searchTerm.trim() || !user || isCreating) return;

    const productName = searchTerm.trim();
    const categoryId = category === "none" ? null : category;

    // Stato di caricamento dedicato (#1503): mostra lo spinner nei pulsanti e
    // impedisce doppi invii mentre la creazione è in corso.
    setIsCreating(true);
    try {
      const sanitizedQuantity = sanitizeQuantity(quantity);

      if (!sanitizedQuantity) {
        return;
      }

      // Passa dal funnel offline-aware (#1503): online scrive su Supabase,
      // offline scrive in Dexie e accoda il sync. Il vecchio `addUserProduct`
      // andava dritto a Supabase e offline si bloccava/falliva in silenzio.
      const newProduct = await createProduct({
        userId: user.id,
        name: productName,
        defaultQuantity: parseFloat(sanitizedQuantity) || 1,
        defaultUnit: unit,
        categoryId: categoryId ?? undefined,
        attributes: [],
      });

      if (!newProduct) throw new Error("Failed to create product");

      const added = await addShoppingItem(listId, {
        product_id: newProduct.id,
        quantity: sanitizedQuantity,
        unit,
        category: newProduct.categoryId ?? categoryId ?? "other",
      });

      if (!added) {
        safeToastError("Impossibile aggiungere il prodotto", {
          description: "Riprova tra qualche istante.",
        });
        return;
      }

      // Stato di rete riletto dopo l'await: tra la creazione del prodotto e qui
      // la connettività può essere cambiata (#1432).
      if (networkService.isOnline()) {
        await queryClient.invalidateQueries({ queryKey: ['shopping-list', listId] });
        await queryClient.invalidateQueries({ queryKey: ['user-products'] });
      }

      setSearchTerm("");
      setQuantity("1");
      setUnit("pz");
      setCategory("none");

      setAddedProductsLocal(prev => [...prev, newProduct.id]);

      onSuccess?.();

      if (closeAfter) {
        handleOpenChange(false);
      }
    } catch (error) {
      console.error("Error creating product:", error);
      // Niente più fallimento muto (#1503): avvisa l'utente in italiano.
      safeToastError("Impossibile creare il prodotto", {
        description: "Riprova tra qualche istante.",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const showOfflineMessage = !isOnline && (!products || products.length === 0);
  const isLoading = productsLoading || categoriesLoading;

  const categoryItems = visibleCategories.map(cat => ({
    ...cat,
    userId: cat.userId || user?.id || ''
  })) as UserCategory[];

  return {
    theme,
    handleOpenChange,
    searchTerm,
    setSearchTerm,
    groupedProducts: filteredGroupedProducts,
    filter,
    setFilter,
    showOfflineMessage,
    isLoading,
    selectedProduct,
    setSelectedProduct,
    quantity,
    setQuantity,
    quantityPresets,
    unit,
    setUnit,
    category,
    setCategory,
    handleProductSelect,
    handleAdd,
    handleAddClose,
    handleCreateProduct,
    isCreating,
    scrollRef,
    addedProductIds,
    addedProductsLocal,
    categoryItems,
    isOnline,
    shoppingItems,
    forceNewProductForm,
  };
};
